/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { includes, isFinite, isNil } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';

import * as layerOps from '../../api/Layers/layer-operations';
import { styleFetchOne } from '../../api/Styles/style-methods-client';
import * as styleOps from '../../api/Styles/style-operations';
import { POINT_TYPES } from '../../api/Styles/Styles';

import addPopupToMapBoxGL from './add-popup-to-mapbox-gl';
import buildGeoImageLayer from './buildGeoImageLayer';
import buildGeoJSONLayer from './buildGeoJSONLayer';
import buildGeoJSONVTLayer from './buildGeoJSONVTLayer';
import buildGeoTiffLayer from './buildGeoTiffLayer';
import buildMapBoxGLLayer from './buildMapBoxGLLayer';
import buildMapBoxLayer from './buildMapBoxLayer';
import buildRealTimeLayer from './buildRealTimeLayer';
import buildTileServerLayer from './buildTileServerLayer';
import buildTMSLayer from './buildTMSLayer';
import buildWMSLayer from './buildWMSLayer';
import buildWMTSLayer from './buildWMTSLayer';
import { scaleIconSize } from '../geojson-layer-style';
import getDefaultStyle from '../get-default-style';

const ARBITRARY_MAX_ZOOM = 20;

/**
 * Draws a single layer in a leaflet map.
 *
 * @async
 * @param {*} arg - The object argument.
 * @param {object} arg.defaultBaseMap - The layer object to draw.
 * @param {object} arg.leafletMap - The leaflet map object.
 * @param {boolean} [arg.isUpdate=false] - A flag to check if is an update call or not.
 * @returns {object} The leaflet layer object added to the map.
 */
const addDefaultBaseMap = async ({ defaultBaseMap, leafletMap, isUpdate = false }) => {
  let newLayerToDraw = '';
  const zIndex = 5;
  if (isUpdate) {
    const layerPane = leafletMap.getPane('5');
    layerPane.removeChild(layerPane.children[0]);
  } else if (isNil(leafletMap.getPane('5'))) {
    leafletMap.createPane('5');
  }
  try {
    const defaultBaseMapStyle =
      (await styleFetchOne(layerOps.getStyleId(defaultBaseMap))) || getDefaultStyle(defaultBaseMap);

    switch (true) {
      case layerOps.isTileServer(defaultBaseMap):
        newLayerToDraw = buildTileServerLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      case layerOps.isWMS(defaultBaseMap):
        newLayerToDraw = buildWMSLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      case layerOps.isWMTS(defaultBaseMap):
        newLayerToDraw = buildWMTSLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      case layerOps.isTMS(defaultBaseMap):
        newLayerToDraw = buildTMSLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      case layerOps.isGeoImage(defaultBaseMap):
        newLayerToDraw = buildGeoImageLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      case layerOps.isGeoJSONApiBased(defaultBaseMap):
        newLayerToDraw = await buildGeoJSONLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex,
          isPartOfMap: true
        });
        break;
      case layerOps.isGeoJSONVT(defaultBaseMap):
        newLayerToDraw = await buildGeoJSONVTLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex,
          isPartOfMap: true
        });
        break;
      case layerOps.isGeoTiff(defaultBaseMap):
        newLayerToDraw = buildGeoTiffLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      case layerOps.isMapBox(defaultBaseMap):
        newLayerToDraw = await buildMapBoxLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex,
          isPartOfMap: true
        });
        break;
      case layerOps.isAnnotationsRealTime(defaultBaseMap):
      case layerOps.isRealTime(defaultBaseMap):
        newLayerToDraw = await buildRealTimeLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex,
          isPartOfMap: true
        });
        break;
      case layerOps.isMapBoxGL(defaultBaseMap):
        newLayerToDraw = buildMapBoxGLLayer({
          layer: defaultBaseMap,
          style: defaultBaseMapStyle,
          zIndex
        });
        break;
      default:
        Bert.alert('Wrong Layer data', 'danger');
        break;
    }

    leafletMap.addLayer(newLayerToDraw);

    if (newLayerToDraw._map._layersMaxZoom !== layerOps.getMaxZoom(defaultBaseMap)) {
      leafletMap.options.minZoom = layerOps.getMinZoom(defaultBaseMap);
      leafletMap.options.maxZoom = layerOps.getMaxZoom(defaultBaseMap);
    }
    if (layerOps.isMapBoxGL(defaultBaseMap))
      addPopupToMapBoxGL({
        newLayerToDraw,
        layer: defaultBaseMap,
        style: defaultBaseMapStyle,
        leafletMap
      });

    if (
      styleOps.isScaledSymbology(defaultBaseMapStyle) &&
      includes([POINT_TYPES.ICON, POINT_TYPES.MARKER], styleOps.getPointType(defaultBaseMapStyle))
    ) {
      let fromZoom = leafletMap.getZoom();
      const maxZoom = isFinite(leafletMap.getMaxZoom())
        ? leafletMap.getMaxZoom()
        : ARBITRARY_MAX_ZOOM;
      const initialScale = leafletMap.getZoomScale(fromZoom, maxZoom);
      leafletMap.on('zoomstart', event => {
        fromZoom = event.target.getZoom();
      });
      const isMarker = styleOps.getPointType(defaultBaseMapStyle) === POINT_TYPES.MARKER;
      scaleIconSize({ newLayerToDraw, scale: initialScale, isMarker });
      leafletMap.on('zoomend', () => {
        const toZoom = leafletMap.getZoom();
        const scale = leafletMap.getZoomScale(toZoom, fromZoom);
        scaleIconSize({ newLayerToDraw, scale, isMarker });
      });
    }
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};

export default addDefaultBaseMap;
